import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Swords, Zap, Shield, Wind, Trophy, Loader2, ChevronRight, Sparkles, Layers, Coins, ChevronLeft } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../lib/api';
import { RARITY_UI } from '../lib/rarity';
import RarityBadge from '../components/RarityBadge';
import { toast } from 'sonner';

export default function Arena() {
  const nav = useNavigate();
  const loc = useLocation();

  // Battle mode: practice / casual / ranked — from path or ?mode=
  const params = new URLSearchParams(loc.search);
  const modeFromPath = loc.pathname.includes('/ranked') ? 'ranked'
    : loc.pathname.includes('/casual') ? 'casual'
    : params.get('mode') || 'practice';
  const [mode, setMode] = useState(modeFromPath);
  const modeLabel = { practice: 'Practice · AI', casual: 'Casual PvP', ranked: 'Ranked PvP' }[mode] || 'Practice · AI';
  const [cards, setCards] = useState([]);
  const [potions, setPotions] = useState([]);
  const [arenas, setArenas] = useState([]);
  const [arenaKey, setArenaKey] = useState(null); // null = Auto (shared habitat)
  const [potionIds, setPotionIds] = useState([]);
  const [equipmentIds, setEquipmentIds] = useState([]);
  const [weather, setWeather] = useState('clear');
  const [equipments, setEquipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [battling, setBattling] = useState(false);
  const [battle, setBattle] = useState(null);
  const [replayIdx, setReplayIdx] = useState(0);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    // Decks removed in iter74 — pick a single card and go.
    Promise.all([
      api.get('/cards?limit=50').then(r => r.data).catch(() => []),
      api.get('/battles/mine?limit=10').then(r => r.data).catch(() => []),
      api.get('/inventory').then(r => r.data).catch(() => ({ potions: [] })),
      api.get('/arenas').then(r => r.data).catch(() => ({ arenas: [] })),
      api.get('/inventory').then(r => r.data).catch(() => ({ equipment: [] })),
    ]).then(([c, h, inv, ar, eq]) => {
      setCards(c); setHistory(h);
      setPotions(inv.potions || []);
      setArenas(ar.arenas || []);
      setEquipments(eq.equipment || []);
      setLoading(false);
    });
  }, []);

  const startBattle = async () => {
    if (!selected) return;
    setBattling(true); setBattle(null); setReplayIdx(0);
    try {
      const body = { challenger_card_id: selected.card_id, best_of: 5, mode, weather };
      // Only pass an explicit arena when user picked one — otherwise let
      // the backend auto-select the ecologically-appropriate shared arena.
      if (arenaKey) body.arena = arenaKey;
      // Practice mode is strictly PvE — force an AI opponent so we never
      // get routed into the PvP "Rival" arena. (Backend also enforces this,
      // but keeping the intent explicit on the client.)
      if (mode === 'practice') body.force_ai_opponent = true;
      if (potionIds.length) body.consumable_ids = potionIds;
      if (equipmentIds.length) body.equipment_ids = equipmentIds;
      const { data } = await api.post('/battles', body);
      // Battle Arena 3.0 — real-time tap combat. Hand off the pre-computed battle
      // to the LiveArena page for interactive playback.
      const consumables = potionIds
        .map(id => potions.find(p => p.potion_id === id))
        .filter(Boolean);
      nav(`/battle/live/${data.battle_id}`, {
        state: { battle: data, consumables },
      });
      return;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Battle failed');
    } finally { setBattling(false); }
  };

  if (loading) return <div className="p-8 text-center text-xs text-muted-foreground"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></div>;

  return (
    <div className="space-y-5 pb-28" data-testid="arena-root">
      <button onClick={() => nav('/battle')} data-testid="arena-back-btn" className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><ChevronLeft className="w-3 h-3" /> Back to Arena Lobby</button>
      <div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-accent">{modeLabel}</p>
        <h2 className="font-serif text-3xl">Battle Cards</h2>
        <p className="text-xs text-muted-foreground mt-1">
          {mode === 'ranked'
            ? 'Every result affects your rating.  Bring your best deck.'
            : mode === 'casual'
              ? 'Warm-up matches with no rating impact.'
              : 'Practice against a randomly-drafted AI opponent.'}
        </p>
      </div>

      {!battle && (
        <>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Choose your fighter</p>
            {cards.length === 0 ? (
              <div className="rounded-3xl bg-card border border-white/5 p-6 text-center text-xs text-muted-foreground">
                Discover any species to mint your first Battle Card.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3" data-testid="arena-card-picker">
                {cards.slice(0, 12).map(c => {
                  const ui = RARITY_UI[c.rarity];
                  const active = selected?.card_id === c.card_id;
                  return (
                    <button
                      key={c.card_id}
                      type="button"
                      data-testid="arena-card-option"
                      onClick={() => setSelected(c)}
                      className={`text-left rounded-2xl p-3 border ${active ? 'ring-2 ring-accent border-accent' : 'border-white/10 hover:border-white/25'} ${ui.cardClass}`}
                      style={ui.cardStyle}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <RarityBadge rarity={c.rarity} size="sm" showParticles={false} />
                        <span className="text-[9px] font-mono text-muted-foreground">{(c.serial_label || '').split(' · ')[0]}</span>
                      </div>
                      <p className="font-serif text-sm truncate">{c.species_common_name}</p>
                      <div className="mt-1 flex items-center justify-between text-[10px]">
                        <span className="text-muted-foreground">Rating</span>
                        <span className="font-mono" style={{ color: ui.color }}>{c.battle_rating}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {selected && (
            <>
              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Choose arena</p>
                <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="arena-selector">
                  <button
                    key="auto"
                    type="button"
                    onClick={() => setArenaKey(null)}
                    data-testid="arena-option-auto"
                    className={`flex-shrink-0 text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-full border ${
                      arenaKey === null ? 'border-emerald-400/70 bg-emerald-500/15 text-emerald-200'
                        : 'border-white/10 bg-card/50 text-muted-foreground hover:text-foreground'
                    }`}
                  >Auto · shared habitat</button>
                  {arenas.map(a => (
                    <button
                      key={a.key}
                      type="button"
                      onClick={() => setArenaKey(a.key)}
                      data-testid={`arena-option-${a.key}`}
                      className={`flex-shrink-0 text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-full border ${
                        arenaKey === a.key ? 'border-accent/60 bg-accent/10 text-accent'
                          : 'border-white/10 bg-card/50 text-muted-foreground hover:text-foreground'
                      }`}
                    >{a.label}</button>
                  ))}
                </div>
                {arenaKey === null ? (
                  <p className="text-[10px] text-emerald-300/90" data-testid="arena-auto-hint">
                    WildQuest picks the ecologically best-fit arena from both species' habitats.
                  </p>
                ) : (() => {
                  const arena = arenas.find(x => x.key === arenaKey);
                  const bonus = arena?.bonuses?.[selected.species_category];
                  return bonus
                    ? <p className="text-[10px] text-emerald-300">Your {selected.species_category} card gets +{Math.round(bonus * 100)}% attack & defense in this arena.</p>
                    : <p className="text-[10px] text-muted-foreground">Neutral arena for your {selected.species_category} card.</p>;
                })()}
              </div>

              {potions.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Optional: prime consumables ({potionIds.length}/2)
                  </p>
                  <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="potion-selector">
                    <button
                      type="button"
                      onClick={() => setPotionIds([])}
                      data-testid="potion-none"
                      className={`flex-shrink-0 text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border ${
                        potionIds.length === 0 ? 'border-accent/60 bg-accent/10 text-accent' : 'border-white/10 bg-card/50 text-muted-foreground'
                      }`}
                    >None</button>
                    {potions.slice(0, 8).map(p => {
                      const active = potionIds.includes(p.potion_id);
                      return (
                        <button
                          key={p.potion_id}
                          type="button"
                          onClick={() => {
                            if (active) return setPotionIds(potionIds.filter(x => x !== p.potion_id));
                            if (potionIds.length >= 2) return toast.error('Max 2 consumables primed per battle');
                            setPotionIds([...potionIds, p.potion_id]);
                          }}
                          data-testid="potion-option"
                          className={`flex-shrink-0 text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border ${
                            active ? 'border-accent/60 bg-accent/10 text-accent'
                              : 'border-white/10 bg-card/50 text-muted-foreground hover:text-foreground'
                          }`}
                        >{p.name}</button>
                      );
                    })}
                  </div>
                </div>
              )}

              {equipments.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Optional: equip gear ({equipmentIds.length}/3)</p>
                  <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="equipment-selector">
                    {equipments.slice(0, 12).map(e => {
                      const active = equipmentIds.includes(e.equipment_id);
                      const ui = RARITY_UI[e.rarity] || RARITY_UI.common;
                      return (
                        <button key={e.equipment_id} type="button"
                          onClick={() => {
                            if (active) return setEquipmentIds(equipmentIds.filter(x => x !== e.equipment_id));
                            if (equipmentIds.length >= 3) return toast.error('Max 3 equipment slots');
                            setEquipmentIds([...equipmentIds, e.equipment_id]);
                          }}
                          data-testid="equipment-option"
                          className={`flex-shrink-0 text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border ${
                            active ? 'border-accent/60 bg-accent/10 text-accent' : 'border-white/10 bg-card/50 text-muted-foreground'
                          }`}
                          title={Object.entries(e.boost || {}).map(([k,v]) => `+${v} ${k}`).join(' · ')}
                          style={active ? undefined : { color: ui.color }}
                        >{e.name}</button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Weather selector — Phase 2 tactical layer */}
              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Weather</p>
                <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="weather-selector">
                  {['clear', 'storm', 'fog', 'rain', 'snow', 'sun', 'twilight'].map(w => (
                    <button
                      key={w}
                      type="button"
                      onClick={() => setWeather(w)}
                      data-testid={`weather-${w}`}
                      className={`flex-shrink-0 text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border capitalize ${
                        weather === w ? 'border-accent/60 bg-accent/10 text-accent'
                          : 'border-white/10 bg-card/50 text-muted-foreground hover:text-foreground'
                      }`}
                    >{w}</button>
                  ))}
                </div>
              </div>

              <button
                onClick={startBattle}
                disabled={battling}
                data-testid="start-battle-btn"
                className="w-full py-4 rounded-full bg-accent text-black font-semibold hover:brightness-110 transition-all flex items-center justify-center gap-2 disabled:opacity-40"
              >
                {battling ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Swords className="w-4 h-4" /> Enter Arena</>}
              </button>
            </>
          )}
        </>
      )}

      {battle && (
        <BattleReplay
          battle={battle}
          replayIdx={replayIdx}
          onReplay={() => setReplayIdx(replayIdx + 1)}
          onNew={() => { setBattle(null); setSelected(null); setReplayIdx(0); }}
        />
      )}

      {!battle && history.length > 0 && (
        <section>
          <h3 className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Recent battles</h3>
          <div className="rounded-3xl bg-card border border-white/5 overflow-hidden">
            {history.map(b => (
              <div key={b.battle_id} className="flex items-center gap-2 p-3 border-b border-white/5 last:border-0 text-xs" data-testid="battle-history-row">
                <span className="truncate flex-1">{b.challenger_snapshot?.name}</span>
                <ChevronRight className="w-3 h-3 text-muted-foreground" />
                <span className="truncate flex-1 text-right">{b.opponent_snapshot?.name}</span>
                <span className={`ml-2 text-[10px] font-mono uppercase ${b.winner === 'challenger' ? 'text-emerald-400' : 'text-red-400'}`}>
                  {b.winner === 'challenger' ? 'Won' : 'Lost'}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function BattleReplay({ battle, replayIdx, onNew }) {
  const rounds = battle.rounds.slice(0, replayIdx);
  const finished = replayIdx >= battle.rounds.length;
  const chWon = battle.winner === 'challenger';
  const uiCh = RARITY_UI[battle.challenger_snapshot?.rarity || 'common'];
  const uiOp = RARITY_UI[battle.opponent_snapshot?.rarity || 'common'];

  return (
    <div className="space-y-4" data-testid="battle-replay">
      <div className="grid grid-cols-2 gap-3">
        <Fighter side="Challenger" snapshot={battle.challenger_snapshot} ui={uiCh} wins={battle.round_wins.challenger} />
        <Fighter side="Opponent"  snapshot={battle.opponent_snapshot}  ui={uiOp} wins={battle.round_wins.opponent} />
      </div>

      {battle.battle_prep && <BattlePrepHeader prep={battle.battle_prep} /> }

      <div className="rounded-3xl bg-card border border-white/5 p-4 space-y-3" data-testid="battle-rounds">
        <AnimatePresence>
          {rounds.map((r, i) => (
            <motion.div
              key={r.round}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl bg-black/30 border border-white/5 p-3"
            >
              <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
                <span>Round {r.round}</span>
                <span className={r.winner === 'challenger' ? 'text-emerald-400' : 'text-red-400'}>
                  {r.winner === 'challenger' ? 'Challenger takes it' : 'Opponent takes it'}
                </span>
              </div>
              <div className="mt-2 space-y-1" data-testid="battle-round-log">
                {(r.log || []).map((line, j) => (
                  <LogLine key={j} line={line} />
                ))}
              </div>
              {(r.status_after?.challenger?.length > 0 || r.status_after?.opponent?.length > 0) && (
                <div className="mt-2 flex flex-wrap gap-1.5 text-[9px]" data-testid="battle-status-row">
                  {(r.status_after?.challenger || []).map((s, i) => (
                    <span key={`c${i}`} className="px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-200">
                      C · {s.name}
                    </span>
                  ))}
                  {(r.status_after?.opponent || []).map((s, i) => (
                    <span key={`o${i}`} className="px-2 py-0.5 rounded-full bg-red-500/15 border border-red-400/30 text-red-200">
                      O · {s.name}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-2 grid grid-cols-2 gap-2 text-[10px]">
                <HpBar label="Challenger" hp={r.hp_after.challenger} max={r.hp_max?.challenger} />
                <HpBar label="Opponent" hp={r.hp_after.opponent} max={r.hp_max?.opponent} />
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {finished && (
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          className={`rounded-3xl p-6 text-center ${chWon ? 'bg-emerald-500/10 border border-emerald-400/40' : 'bg-red-500/10 border border-red-400/40'}`}
          data-testid="battle-result"
        >
          <Trophy className={`w-8 h-8 mx-auto mb-2 ${chWon ? 'text-emerald-400' : 'text-red-400'}`} />
          <p className="text-[10px] uppercase tracking-[0.3em]">{chWon ? 'Victory' : 'Defeat'}</p>
          <p className="font-serif text-2xl mt-2">
            {battle.round_wins.challenger} – {battle.round_wins.opponent}
          </p>
          <button onClick={onNew} data-testid="new-battle-btn" className="mt-4 px-5 py-2 rounded-full bg-white/10 hover:bg-white/15 text-xs uppercase tracking-widest">
            <Sparkles className="w-3 h-3 inline mr-1" /> New Battle
          </button>
        </motion.div>
      )}
    </div>
  );
}

function Fighter({ side, snapshot, ui, wins }) {
  return (
    <div className={`rounded-2xl p-3 ${ui.cardClass}`} style={ui.cardStyle}>
      <p className="text-[9px] uppercase tracking-widest text-muted-foreground">{side}</p>
      <p className="font-serif text-base truncate mt-0.5">{snapshot?.name}</p>
      <div className="flex items-center justify-between mt-2 text-[10px]">
        <span className="font-mono" style={{ color: ui.color }}>Rating {snapshot?.battle_rating}</span>
        <span className="font-mono text-accent">Wins {wins}</span>
      </div>
    </div>
  );
}

function LogLine({ line }) {
  if (!line) return null;
  const isRoundHeader = line.startsWith('— Round ');
  const isCombo = line.toLowerCase().includes('combo!');
  const isEquipReact = line.startsWith('— ') && !isRoundHeader && !isCombo && !line.toLowerCase().includes('takes the round');
  const isTakes = line.toLowerCase().includes('takes the round');
  const isCrit = line.toLowerCase().includes('critical');
  const isDodge = line.toLowerCase().includes('dodge') || line.toLowerCase().includes('slips') || line.toLowerCase().includes('evade') || line.toLowerCase().includes('melts');
  const isCounter = line.toLowerCase().includes('counter');
  const cls = isRoundHeader ? 'text-[10px] uppercase tracking-widest text-accent border-b border-white/5 pb-1'
    : isCombo ? 'text-xs text-fuchsia-300 font-semibold italic'
    : isEquipReact ? 'text-[11px] text-emerald-300/80 italic'
    : isTakes ? 'text-[10px] uppercase tracking-widest text-white/80 border-t border-white/5 pt-1'
    : isCrit ? 'text-xs text-amber-300 font-semibold'
    : isDodge ? 'text-xs text-cyan-300'
    : isCounter ? 'text-xs text-red-300'
    : 'text-xs text-muted-foreground';
  return <p className={cls}>{line}</p>;
}

function BattlePrepHeader({ prep }) {
  if (!prep) return null;
  const chips = [];
  if (prep.arena) chips.push({ label: prep.arena, kind: 'arena' });
  if (prep.weather && prep.weather !== 'clear') chips.push({ label: prep.weather, kind: 'weather' });
  if (prep.season) chips.push({ label: prep.season, kind: 'season' });
  const chP = prep.passives?.challenger || [];
  const opP = prep.passives?.opponent || [];
  const equipment = prep.equipment || [];
  return (
    <div className="rounded-2xl border border-white/5 bg-black/30 p-3 space-y-2" data-testid="battle-prep-header">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Battlefield</p>
      <div className="flex flex-wrap gap-1.5">
        {chips.map((c, i) => (
          <span key={i} data-testid={`prep-chip-${c.kind}`}
            className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest capitalize bg-white/5 border border-white/10 text-white/80">
            {c.kind} · {c.label}
          </span>
        ))}
      </div>
      {(chP.length > 0 || opP.length > 0) && (
        <div className="grid grid-cols-2 gap-2 pt-1">
          <div>
            <p className="text-[9px] uppercase tracking-widest text-emerald-300/70 mb-1">Challenger passives</p>
            <div className="flex flex-wrap gap-1">
              {chP.length === 0 && <span className="text-[10px] text-muted-foreground">—</span>}
              {chP.map((p, i) => (
                <span key={i} className="px-1.5 py-0.5 rounded-md text-[9px] bg-emerald-500/10 border border-emerald-400/20 text-emerald-200">{p.name}</span>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-widest text-red-300/70 mb-1">Opponent passives</p>
            <div className="flex flex-wrap gap-1">
              {opP.length === 0 && <span className="text-[10px] text-muted-foreground">—</span>}
              {opP.map((p, i) => (
                <span key={i} className="px-1.5 py-0.5 rounded-md text-[9px] bg-red-500/10 border border-red-400/20 text-red-200">{p.name}</span>
              ))}
            </div>
          </div>
        </div>
      )}
      {equipment.length > 0 && (
        <div>
          <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1">Equipment</p>
          <div className="flex flex-wrap gap-1">
            {equipment.map((e, i) => (
              <span key={i} className="px-1.5 py-0.5 rounded-md text-[9px] bg-white/5 border border-white/10 text-white/70">{e}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function HpBar({ label, hp, max }) {
  const denom = max || 200;
  const pct = Math.max(0, Math.min(100, (hp / denom) * 100));
  return (
    <div>
      <div className="flex justify-between text-[9px] uppercase tracking-widest text-muted-foreground">
        <span>{label}</span><span>{hp}{max ? `/${max}` : ''}</span>
      </div>
      <div className="h-1 rounded-full bg-white/5 overflow-hidden mt-0.5">
        <motion.div
          className={`h-full ${pct > 50 ? 'bg-gradient-to-r from-emerald-400 to-emerald-300' : pct > 25 ? 'bg-gradient-to-r from-amber-400 to-amber-300' : 'bg-gradient-to-r from-red-500 to-red-400'}`}
          initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.5 }}
        />
      </div>
    </div>
  );
}
